import React, { createContext, useContext, useMemo, useState } from 'react'
import Toast from '@taglivros/oef_componentes/build/components/toast'
import { getDadosAssociado, patchDadosAssociado } from '../api/associado.servico'
import { postEsqueciSenha, postNovaSenhaLogado, postNovaSenhaNaoLogado } from '../api/senha.servico'
import { useGlobal } from '../contexts/GlobalContext'
import { isEmptyObject, objetoTemPropriedade } from '../utils/helpers/helpers'
import { FALHA_ESQUECI_SENHA } from '../containers/EsqueciMinhaSenha/constantsEsqueciMinhaSenha'
import { setToken } from '../api/autenticacao.servico'
import { validateCaptcha, resetCaptcha } from '../utils/recaptcha'

const AssociadoContext = createContext()

function AssociadoProvider( props ) {
  const [ state, setState ] = useState( {
    'dadosAssociado': {},
    'loadingRequisicaoSenha': false,
    'openFormularioDadosPessoais': false,
    'openFormularioSenha': false,
    'sucessoEsqueciMinhaSenha': false
  } )

  const value = useMemo( () => {
    return {
      setState,
      state
    }
  }, [ state ] )

  return (
    <AssociadoContext.Provider
      value={ value }
      { ...props }
    />
  )
}

function useAssociado() {
  const context = useContext( AssociadoContext )
  if ( !context ) {
    throw new Error( 'useAssociado deve ser usado dentro de um AssociadoProvider' )
  }

  const { state, setState } = context
  const { setErroRequisicao, setErroEsqueciSenha, setToastGeral, setCaptcha } = useGlobal()

  const setDadosAssociado = dadosAssociado => {
    setState( prevState => ( {
      ...prevState,
      dadosAssociado
    } ) )
  }

  const setToggleFormAlterarDados = flag => {
    setState( prevState => ( {
      ...prevState,
      'openFormularioDadosPessoais': flag
    } ) )
  }

  const setToggleFormAlterarSenha = flag => {
    setState( prevState => ( {
      ...prevState,
      'openFormularioSenha': flag
    } ) )
  }

  const setLoadingRequisicaoSenha = loadingRequisicaoSenha => {
    setState( prevState => ( {
      ...prevState,
      loadingRequisicaoSenha
    } ) )
  }

  const setSucessoEsqueciMinhaSenha = sucessoEsqueciMinhaSenha => {
    setState( prevState => ( {
      ...prevState,
      sucessoEsqueciMinhaSenha
    } ) )
  }

  const fetchDadosAssociado = async() => {
    try {
      const response = await getDadosAssociado()
      if ( !isEmptyObject( response.data ) ) {
        setDadosAssociado( response.data )
      }
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    }
  }

  const postRedefinirSenhaLogado = async( senhas ) => {
    setLoadingRequisicaoSenha( true )
    try {
      const { 'data': dados } = await postNovaSenhaLogado( senhas )
      if ( !isEmptyObject( dados ) ) {
        setLoadingRequisicaoSenha( false )
        setToggleFormAlterarSenha( false )
        setToastGeral( {
          'mensagem': 'Senha alterada com sucesso!',
          'show': true,
          'tipoToast': Toast.SUCCESS
        } )
      }
    } catch ( erro ) {
      const { message } = erro.response.data.errors[ 0 ] || null
      if ( message ) {
        const descricaoErro = {
          'descricao': message,
          'titulo': 'Houve um erro!'
        }
        setErroRequisicao(
          erro.response.status || erro.request.status,
          descricaoErro
        )
      } else {
        setErroRequisicao( erro.response.status )
      }
      setLoadingRequisicaoSenha( false )
    }
  }

  const postRedefinirSenhaNaoLogado = async( senhas ) => {
    setLoadingRequisicaoSenha( true )
    try {
      const { 'data': dados } = await postNovaSenhaNaoLogado( senhas )
      setLoadingRequisicaoSenha( false )
      return !isEmptyObject( dados ) && objetoTemPropriedade( dados, 'token' )
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
      return false
    }
  }

  const atualizarDadosAssociado = async dados => {
    try {
      const response = await patchDadosAssociado( dados )
      if ( !isEmptyObject( response ) ) {
        if ( response.data.token ) {
          setToken( response.data.token )
        }
        fetchDadosAssociado()
        setToggleFormAlterarDados( false )
      }
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    }
  }

  const postEsqueciSenhaNaoLogado = async( email, redirectTo ) => {
    try {
      const captcha = validateCaptcha()
      const { 'data': dados } = await postEsqueciSenha( email, captcha, redirectTo )
      if ( dados ) {
        setSucessoEsqueciMinhaSenha( true )
      }
    } catch ( erro ) {
      resetCaptchaToken()
      setSucessoEsqueciMinhaSenha( false )
      if ( erro.response ) {
        setErroEsqueciSenha( erro.response )
      } else {
        setToastGeral( {
          'mensagem': FALHA_ESQUECI_SENHA,
          'show': true,
          'tipoToast': Toast.ERROR
        } )
      }
    }
  }

  const resetCaptchaToken = () => {
    resetCaptcha()
    setCaptcha( null )
  }

  return {
    atualizarDadosAssociado,
    'dadosAssociado': state.dadosAssociado,
    fetchDadosAssociado,
    'loadingRequisicaoSenha': state.loadingRequisicaoSenha,
    'openFormularioDadosPessoais': state.openFormularioDadosPessoais,
    'openFormularioSenha': state.openFormularioSenha,
    postEsqueciSenhaNaoLogado,
    postRedefinirSenhaLogado,
    postRedefinirSenhaNaoLogado,
    setToggleFormAlterarDados,
    setToggleFormAlterarSenha,
    'sucessoEsqueciMinhaSenha': state.sucessoEsqueciMinhaSenha
  }
}

export { AssociadoProvider, useAssociado }
